import {
  required,
  email,
  minLength,
  sameAs,
  not,
} from 'vuelidate/lib/validators';
import { forcePasswordPolicy, forceUsernamePolicy } from '@/app/utils/validations/validations';
import ConfirmCode from '@/app/modules/auth/components/confirm-code/confirm-code.vue';
import { API, ENDPOINTS } from '@/app/services/api-url';
import { SUBSCRIPTION_APPS } from '@/app/utils/app/app';

export default {
  name: 'register',
  components: {
    ConfirmCode,
  },
  data: () => ({
    hasInviteOrShare: false,
    hasConfirmCode: false,
    confirmCode: '',
    specialChars: '^$*.[]{}()?"!@#%&/\\,><\':;|_~`',
    signUpForm: {
      username: '',
      nickname: '',
      given_name: '',
      family_name: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
  }),
  computed: {
    hasAuthError() {
      return this.$store.state.auth.hasAuthError;
    },
    authError() {
      return this.$store.state.auth.authError;
    },
    hasInviteOrShareQueryParams() {
      const queryParams = this.$route.query;
      return !!(queryParams.email && queryParams.firstName && queryParams.lastName);
    },
    hasPurchaseQueryParam() {
      const queryParams = this.$route.query;
      return !!queryParams.purchase;
    },
    hasValidPurchaseQueryParam() {
      const queryParams = this.$route.query;
      return !!(this.hasPurchaseQueryParam && SUBSCRIPTION_APPS[queryParams.purchase]);
    },
  },
  methods: {
    async handleClick(value) {
      if (value === 'cancel') {
        this.hasConfirmCode = false;
      } else if (value === 'submit') {
        try {
          await this.$store.dispatch('auth/confirmSignUp', {
            username: this.signUpForm.username,
            code: this.confirmCode.toString(),
          });

          this.$mixpanel.track('registerConfirmed', {
            username: this.signUpForm.username,
            $email: this.signUpForm.email.toLowerCase(),
          });

          await this.$store.dispatch('auth/login', {
            username: this.signUpForm.username,
            password: this.signUpForm.password,
          });

          const URL_MAP = {
            default: '/on-board',
            invite: `/invite?action=accept&inviteID=${this.$route.query.inviteID}`,
            share: `/on-board?action=accept&shareID=${this.$route.query.shareID}`,
            purchase: `/on-board?action=purchase&product=${this.$route.query.purchase}`,
          };

          let type = 'default';
          if (this.$route.query.inviteID || this.$route.query.shareID) {
            type = this.$route.query.inviteID ? 'invite' : 'share';
          } else if (this.hasValidPurchaseQueryParam) {
            type = 'purchase';
          }
          this.$router.push(URL_MAP[type]);
        } catch (err) {
          console.log(err);
          this.hasConfirmCode = false;
        }
      }
    },
    async register() {
      await this.$v.signUpForm.$touch();
      if (!this.$v.signUpForm.$anyError) {
        const user = await this.$store.dispatch('auth/signUp', {
          username: this.signUpForm.username,
          nickname: this.signUpForm.username, // Temp measure as we need nickname in the future
          givenName: this.signUpForm.given_name,
          familyName: this.signUpForm.family_name,
          email: this.signUpForm.email.toLowerCase(),
          password: this.signUpForm.password,
        });
        if (!this.hasAuthError) {
          this.hasConfirmCode = true;
        }
        if (user) {
          this.$mixpanel.alias(user.userSub);
        }
        let userType;
        if (this.hasInviteOrShare) {
          if (this.$route.query.inviteID) {
            userType = 'invite';
          } else {
            userType = 'share';
          }
        } else {
          userType = 'self';
        }

        this.$mixpanel.people.set({
          $first_name: this.signUpForm.given_name,
          $last_name: this.signUpForm.family_name,
          nickname: this.signUpForm.username,
          username: this.signUpForm.username,
          user_type: userType,
          $email: this.signUpForm.email.toLowerCase(),
          createdAt: new Date().toISOString(),
        });
        this.$mixpanel.people.set_once({
          privacyPolicyAccepted: true,
          'utm_source (first touch)': this.$cookies.get('utm_source'),
          'utm_medium (first touch)': this.$cookies.get('utm_medium'),
          'utm_campaign (first touch)': this.$cookies.get('utm_campaign'),
          self_registered: true,
          register_user_type: userType,
        });
        this.$mixpanel.track('register', {
          username: this.signUpForm.username,
          $email: this.signUpForm.email.toLowerCase(),
        });
      }
    },
    handleInviteOrShareParams() {
      const queryParams = this.$route.query;
      if (queryParams.email) {
        this.signUpForm.email = queryParams.email;
        this.$v.signUpForm.email.$touch();
      }

      if (queryParams.firstName) {
        this.signUpForm.given_name = queryParams.firstName;
        this.$v.signUpForm.given_name.$touch();
      }

      if (queryParams.lastName) {
        this.signUpForm.family_name = queryParams.lastName;
        this.$v.signUpForm.family_name.$touch();
      }
    },
  },
  mounted() {
    this.$mixpanel.register({
      environment: process.env.VUE_APP_ENV,
      eventAt: new Date().toISOString(),
    });

    if (!this.hasConfirmCode && !this.$store.getters['auth/loggedIn']) {
      this.$mixpanel.track('registerInitiated');
    }

    if (this.$route && this.$route.query && Object.keys(this.$route.query).length > 0) {
      if (this.hasInviteOrShareQueryParams) {
        this.hasInviteOrShare = true;
        this.handleInviteOrShareParams();
      }
    }
  },
  validations: {
    signUpForm: {
      username: {
        required,
        minLength: minLength(8),
        email: not(email),
        forceUsernamePolicy,
        async isUnique(value) {
          if (
            value === ''
            || this.$v.signUpForm.username.minLength === false
            || this.$v.signUpForm.username.forceUsernamePolicy === false
          ) return true;
          try {
            const postData = {
              username: value,
              inviteID: this.$route.query.inviteID,
              shareID: this.$route.query.shareID,
            };
            const res = await API.post(ENDPOINTS.POST_USEREXISTS, postData);
            return !res.data.data.userExists;
          } catch (err) {
            return true;
          }
        },
      },
      given_name: {
        required,
        minLength: minLength(2),
      },
      family_name: {
        required,
        minLength: minLength(2),
      },
      email: {
        required,
        email,
        async isUnique(value) {
          if (value === '' || this.$v.signUpForm.email.email === false) return true;
          try {
            const postData = {
              email: value.toLowerCase(),
              inviteID: this.$route.query.inviteID,
              shareID: this.$route.query.shareID,
            };
            const res = await API.post(ENDPOINTS.POST_USEREXISTS, postData);
            return !res.data.data.userExists;
          } catch (err) {
            return true;
          }
        },
      },
      password: {
        required,
        minLength: minLength(8),
        forcePasswordPolicy,
      },
      confirmPassword: {
        sameAsPassword: sameAs('password'),
      },
    },
  },
};
